/**
 * Logger Utility
 * Environment-aware logging system for development and production
 *
 * In production:
 * - Only errors are logged
 * - Sensitive information is filtered
 * - Logs can be sent to error tracking service (e.g., Sentry)
 *
 * In development:
 * - All log levels are shown
 * - Full context is preserved for debugging
 */

const isDevelopment = process.env.NODE_ENV === 'development';

export const logger = {
  /**
   * Info level - general information (dev only)
   */
  info: (message: string, ...args: unknown[]): void => {
    if (isDevelopment) {
      console.log(`[INFO] ${message}`, ...args);
    }
  },

  /**
   * Debug level - detailed debugging info (dev only)
   */
  debug: (message: string, ...args: unknown[]): void => {
    if (isDevelopment) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  },

  /**
   * Warning level - potential issues (dev only)
   */
  warn: (message: string, ...args: unknown[]): void => {
    if (isDevelopment) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  },

  /**
   * Error level - errors and exceptions (always logged)
   * In production, these should be sent to error tracking service
   */
  error: (message: string, ...args: unknown[]): void => {
    console.error(`[ERROR] ${message}`, ...args);

    // TODO: In production, send to error tracking service
    // Example: Sentry.captureException(error)
    if (!isDevelopment) {
      // Production error tracking integration point
      // e.g., Sentry.captureMessage(message, { extra: args });
    }
  },
};
